import { useCallback, useEffect, useRef, useState } from "react";

import { askZhatura, type AssistantReply } from "../api";
import { ui } from "../content/en";

interface Props {
  readonly credential: string;
  readonly stepKey: string;
  readonly onClose: () => void;
}

/**
 * Journey-scoped assistant.
 *
 * Rendered as a native modal dialog so focus containment, Escape handling and
 * background inertness come from the platform rather than from hand-written
 * keyboard code that is easy to get subtly wrong.
 */
export function AskZhaturaPanel({
  credential,
  stepKey,
  onClose,
}: Props): React.JSX.Element {
  const [question, setQuestion] = useState("");
  const [pending, setPending] = useState(false);
  const [reply, setReply] = useState<AssistantReply>();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  const dismiss = useCallback((): void => {
    const dialog = dialogRef.current;
    if (dialog !== null && typeof dialog.close === "function") {
      dialog.close();
      return;
    }
    // Environments without the dialog element still get a working close.
    dialog?.removeAttribute("open");
    onClose();
  }, [onClose]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return undefined;
    if (typeof dialog.showModal === "function") {
      dialog.showModal();
    } else {
      dialog.setAttribute("open", "");
    }
    inputRef.current?.focus();
    // Dismiss on a click in the backdrop area. Bound natively so the dialog
    // element keeps its platform semantics.
    function onBackdropClick(event: MouseEvent): void {
      if (event.target === dialog) dismiss();
    }
    dialog.addEventListener("click", onBackdropClick);
    return () => {
      dialog.removeEventListener("click", onBackdropClick);
    };
  }, [dismiss]);

  async function ask(): Promise<void> {
    const bounded = question.trim();
    if (bounded.length === 0 || pending) return;
    setPending(true);
    setReply(undefined);
    try {
      setReply(await askZhatura(credential, bounded, stepKey));
    } catch {
      // A failing assistant degrades to the approved refusal, never to silence.
      setReply({ outcome: "UNAVAILABLE", answer: null });
    } finally {
      setPending(false);
    }
  }

  const answer =
    reply === undefined
      ? null
      : reply.outcome === "ANSWERED" && reply.answer !== null
        ? reply.answer
        : ui.askUnavailable;

  return (
    <dialog
      className="ask-dialog"
      ref={dialogRef}
      aria-labelledby="ask-title"
      aria-describedby="ask-intro"
      onClose={onClose}
    >
      <div className="ask-panel">
        <div className="ask-head">
          <div>
            <h2 id="ask-title">{ui.askZhaturaTitle}</h2>
            <p id="ask-intro" className="footnote">
              {ui.askZhaturaIntro}
            </p>
          </div>
          <button type="button" className="btn quiet small" onClick={dismiss}>
            {ui.askClose}
          </button>
        </div>
        <label htmlFor="ask-input" className="section-title">
          {ui.askPlaceholder}
        </label>
        <textarea
          id="ask-input"
          ref={inputRef}
          value={question}
          maxLength={500}
          onChange={(event) => {
            setQuestion(event.target.value);
          }}
        />
        <button
          type="button"
          className="btn primary"
          disabled={pending || question.trim().length === 0}
          onClick={() => {
            void ask();
          }}
        >
          {pending ? ui.askThinking : ui.askSubmit}
        </button>
        <div aria-live="polite">
          {answer !== null && <p className="ask-answer">{answer}</p>}
        </div>
      </div>
    </dialog>
  );
}
